import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import AnimateHeight from "react-animate-height";
import { useDebouncedCallback } from "use-debounce";
import { AreaChart, WealthCalculatorPeriodAdjustment } from "../../../shared/components/Graphs/Area/AreaGraph";
import { BlockContentRenderer } from "../BlockContentRenderer";
import { LoadingButtonSpinner } from "../../../shared/components/Spinner/LoadingButtonSpinner";
import { wealthMountainGraphData } from "./data";
import styles from "./WealthCalculator.module.scss";
import { WealthCalculatorInput, WealthCalculatorInputConfiguration } from "./WealthCalculatorInput";
import { TaxJurisdiction, calculateWealthPercentile, equvivalizeIncome, getCachedPostTaxIncome, getEstimatedPostTaxIncome } from "./_util";
import { WealthCalculatorSlider, WealthCalculatorSliderConfig } from "./WealthCalculatorSlider";
import { WealthCalculatorImpact, WealthCalculatorImpactConfig } from "./WealthCalculatorImpact";
import { AdjustedPPPFactorResult, getDanishAdjustedPPPconversionFactor, getNorwegianAdjustedPPPconversionFactor, getSwedishAdjustedPPPconversionFactor } from "./_queries";
export type WealthCalculatorConfiguration = {
  calculator_input_configuration: WealthCalculatorInputConfiguration;
  slider_configuration: WealthCalculatorSliderConfig;
  income_percentile_label_template_string: string;
  income_percentile_after_donation_label_template_string: string;
  default_donation_percentage?: number;
  data_explanation_label?: string;
  data_explanation?: any;
  x_axis_label?: string;
  chart_label?: string;
};
type WealthCalculatorProps = {
  title: string;
  configuration: WealthCalculatorConfiguration;
  impactConfiguration?: WealthCalculatorImpactConfig;
  periodAdjustment: WealthCalculatorPeriodAdjustment;
  locale: string;
};
export const WealthCalculator: React.FC<WealthCalculatorProps> = t0 => {
  const $ = _c(85);
  const {
    title,
    configuration,
    impactConfiguration,
    periodAdjustment,
    locale
  } = t0;
  const {
    calculator_input_configuration,
    slider_configuration,
    income_percentile_label_template_string,
    income_percentile_after_donation_label_template_string,
    default_donation_percentage,
    data_explanation_label,
    data_explanation,
    x_axis_label,
    chart_label
  } = configuration;
  const [incomeInputs, setIncomeInputs] = useState();
  let t1;
  if ($[0] !== incomeInputs) {
    t1 = incomeInputs || [0];
    $[0] = incomeInputs;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const incomes = t1;
  const [numberOfChildren, setNumberOfChildren] = useState(0);
  const [numberOfAdults, setNumberOfParents] = useState(1);
  const [donationPercentage, setDonationPercentage] = useState(default_donation_percentage || 10);
  const [loadingPostTaxIncome, setLoadingPostTaxIncome] = useState(false);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      postTaxIncome: 0,
      numberOfAdults: 1
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [postTaxEstimate, setPostTaxEstimate] = useState(t2);
  const postTaxIncome = postTaxEstimate.postTaxIncome;
  const latestEstimateRequest = useRef(0);
  const [explanationOpen, setExplanationOpen] = useState(false);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      adjustedPPPfactor: 7,
      cumulativeInflation: 0,
      pppFactor: 7
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [pppConversion, setPppConversion] = useState(t3);
  let t4;
  if ($[4] !== locale) {
    t4 = () => {
      if (locale === "no") {
        getNorwegianAdjustedPPPconversionFactor().then(res => {
          setPppConversion(res);
        });
      } else {
        if (locale === "sv") {
          getSwedishAdjustedPPPconversionFactor().then(res_0 => {
            setPppConversion(res_0);
          });
        } else {
          if (locale === "dk") {
            getDanishAdjustedPPPconversionFactor().then(res_1 => {
              setPppConversion(res_1);
            });
          } else {
            console.error("Unsupported locale", locale);
          }
        }
      }
    };
    $[4] = locale;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [setPppConversion];
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  useEffect(t4, t5);
  const taxJurisdiction = locale === "no" ? TaxJurisdiction.NO : locale === "sv" ? TaxJurisdiction.SV : locale === "dk" ? TaxJurisdiction.DK : undefined;
  const sumIncomes = _temp2;
  let t6;
  if ($[7] !== incomes || $[8] !== locale || $[9] !== periodAdjustment || $[10] !== taxJurisdiction) {
    t6 = (requestId, adults) => {
      if (!taxJurisdiction) {
        console.error("Unsupported locale", locale);
        return;
      }
      Promise.all(incomes.map(adultIncome => getEstimatedPostTaxIncome(adultIncome, periodAdjustment, taxJurisdiction))).then(postTaxIncomes => {
        if (requestId !== latestEstimateRequest.current) {
          return;
        }
        setPostTaxEstimate({
          postTaxIncome: sumIncomes(postTaxIncomes),
          numberOfAdults: adults
        });
      }).catch(_temp3).finally(() => {
        if (requestId === latestEstimateRequest.current) {
          setLoadingPostTaxIncome(false);
        }
      });
    };
    $[7] = incomes;
    $[8] = locale;
    $[9] = periodAdjustment;
    $[10] = taxJurisdiction;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const calculatePostTaxIncome = useDebouncedCallback(t6, 250);
  let t7;
  if ($[12] !== calculatePostTaxIncome || $[13] !== incomes || $[14] !== numberOfAdults || $[15] !== periodAdjustment || $[16] !== taxJurisdiction) {
    t7 = () => {
      const requestId_0 = latestEstimateRequest.current = latestEstimateRequest.current + 1;
      const cached = taxJurisdiction ? incomes.map(adultIncome_0 => getCachedPostTaxIncome(adultIncome_0, periodAdjustment, taxJurisdiction)) : [];
      if (taxJurisdiction && cached.every(_temp4)) {
        calculatePostTaxIncome.cancel();
        setPostTaxEstimate({
          postTaxIncome: sumIncomes(cached as number[]),
          numberOfAdults
        });
        setLoadingPostTaxIncome(false);
        return;
      }
      setLoadingPostTaxIncome(true);
      calculatePostTaxIncome(requestId_0, numberOfAdults);
    };
    $[12] = calculatePostTaxIncome;
    $[13] = incomes;
    $[14] = numberOfAdults;
    $[15] = periodAdjustment;
    $[16] = taxJurisdiction;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== incomeInputs || $[19] !== numberOfAdults) {
    t8 = [incomeInputs, numberOfAdults];
    $[18] = incomeInputs;
    $[19] = numberOfAdults;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  useEffect(t7, t8);
  const equvivalizedIncome = equvivalizeIncome(postTaxIncome, numberOfChildren, postTaxEstimate.numberOfAdults);
  let t9;
  if ($[21] !== calculator_input_configuration || $[22] !== incomeInputs || $[23] !== loadingPostTaxIncome || $[24] !== numberOfAdults || $[25] !== numberOfChildren || $[26] !== title) {
    t9 = <WealthCalculatorInput title={title} incomeInput={incomeInputs} setIncomeInput={setIncomeInputs} numberOfChildren={numberOfChildren} setNumberOfChildren={setNumberOfChildren} numberOfAdults={numberOfAdults} setNumberOfParents={setNumberOfParents} loadingPostTaxIncome={loadingPostTaxIncome} config={calculator_input_configuration} />;
    $[21] = calculator_input_configuration;
    $[22] = incomeInputs;
    $[23] = loadingPostTaxIncome;
    $[24] = numberOfAdults;
    $[25] = numberOfChildren;
    $[26] = title;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== donationPercentage || $[29] !== equvivalizedIncome || $[30] !== periodAdjustment || $[31] !== postTaxIncome || $[32] !== pppConversion.adjustedPPPfactor || $[33] !== slider_configuration) {
    t10 = <WealthCalculatorSlider donationPercentage={donationPercentage} setDonationPercentage={setDonationPercentage} postTaxIncome={postTaxIncome} wealthMountainGraphData={wealthMountainGraphData} equvivalizedIncome={equvivalizedIncome} periodAdjustment={periodAdjustment} adjustedPppFactor={pppConversion.adjustedPPPfactor} config={slider_configuration} />;
    $[28] = donationPercentage;
    $[29] = equvivalizedIncome;
    $[30] = periodAdjustment;
    $[31] = postTaxIncome;
    $[32] = pppConversion.adjustedPPPfactor;
    $[33] = slider_configuration;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  const t11 = equvivalizedIncome || 0;
  const t12 = donationPercentage / 100;
  const t13 = equvivalizedIncome || 0;
  const t14 = pppConversion?.adjustedPPPfactor;
  let t15;
  if ($[35] !== periodAdjustment || $[36] !== t13 || $[37] !== t14) {
    t15 = calculateWealthPercentile(wealthMountainGraphData, t13, periodAdjustment, t14);
    $[35] = periodAdjustment;
    $[36] = t13;
    $[37] = t14;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  const t16 = equvivalizedIncome * (1 - donationPercentage / 100);
  const t17 = pppConversion?.adjustedPPPfactor;
  let t18;
  if ($[39] !== periodAdjustment || $[40] !== t16 || $[41] !== t17) {
    t18 = calculateWealthPercentile(wealthMountainGraphData, t16, periodAdjustment, t17);
    $[39] = periodAdjustment;
    $[40] = t16;
    $[41] = t17;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  const t19 = pppConversion?.adjustedPPPfactor;
  let t20;
  if ($[43] !== chart_label || $[44] !== income_percentile_after_donation_label_template_string || $[45] !== income_percentile_label_template_string || $[46] !== periodAdjustment || $[47] !== t11 || $[48] !== t12 || $[49] !== t15 || $[50] !== t18 || $[51] !== t19) {
    t20 = <div className={styles.calculator__output__chart}><AreaChart data={wealthMountainGraphData} lineInput={t11} donationPercentage={t12} wealthPercentile={t15} afterDonationWealthPercentile={t18} label={chart_label} afterDonationPercentileLabelTemplateString={income_percentile_after_donation_label_template_string} incomePercentileLabelTemplateString={income_percentile_label_template_string} adjustedPPPConversionFactor={t19} periodAdjustment={periodAdjustment} /></div>;
    $[43] = chart_label;
    $[44] = income_percentile_after_donation_label_template_string;
    $[45] = income_percentile_label_template_string;
    $[46] = periodAdjustment;
    $[47] = t11;
    $[48] = t12;
    $[49] = t15;
    $[50] = t18;
    $[51] = t19;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] !== loadingPostTaxIncome) {
    t21 = loadingPostTaxIncome && <div className={styles.calculator__output__spinner} data-cy="wealthcalculator-loading-spinner"><LoadingButtonSpinner /></div>;
    $[53] = loadingPostTaxIncome;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== t20 || $[56] !== t21) {
    t22 = <div className={styles.calculator__output} data-cy="wealthcalculator-graph">{t20}{t21}</div>;
    $[55] = t20;
    $[56] = t21;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  const t23 = styles.calculator__explanation__toggle + " " + (explanationOpen ? styles.calculator__explanation__toggle_open : "");
  let t24;
  if ($[58] !== explanationOpen) {
    t24 = () => setExplanationOpen(!explanationOpen);
    $[58] = explanationOpen;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  const t25 = data_explanation_label || "Explanation";
  let t26;
  if ($[60] !== t23 || $[61] !== t24 || $[62] !== t25) {
    t26 = <div className={t23} data-cy="wealthcalculator-explanation-toggle" onClick={t24}>{t25}</div>;
    $[60] = t23;
    $[61] = t24;
    $[62] = t25;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  const t27 = x_axis_label || "Yearly income (log scale)";
  let t28;
  if ($[64] !== t27) {
    t28 = <div className={styles.calculator__axis__label}><span>{t27} →</span></div>;
    $[64] = t27;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  let t29;
  if ($[66] !== t10 || $[67] !== t22 || $[68] !== t26 || $[69] !== t28 || $[70] !== t9) {
    t29 = <div className={styles.calculator} data-cy="wealthcalculator-container">{t9}{t10}{t22}{t26}{t28}</div>;
    $[66] = t10;
    $[67] = t22;
    $[68] = t26;
    $[69] = t28;
    $[70] = t9;
    $[71] = t29;
  } else {
    t29 = $[71];
  }
  const t30 = explanationOpen ? "auto" : 0;
  let t31;
  if ($[72] !== data_explanation) {
    t31 = <div data-cy="wealthcalculator-explanation"><BlockContentRenderer content={[data_explanation]} /></div>;
    $[72] = data_explanation;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  let t32;
  if ($[74] !== t30 || $[75] !== t31) {
    t32 = <AnimateHeight height={t30} duration={500}>{t31}</AnimateHeight>;
    $[74] = t30;
    $[75] = t31;
    $[76] = t32;
  } else {
    t32 = $[76];
  }
  let t33;
  if ($[77] !== donationPercentage || $[78] !== impactConfiguration || $[79] !== postTaxIncome) {
    t33 = impactConfiguration && <WealthCalculatorImpact donationPercentage={donationPercentage} setDonationPercentage={setDonationPercentage} postTaxIncome={postTaxIncome} config={{
      ...impactConfiguration,
      intervention_configuration: {
        ...impactConfiguration.intervention_configuration,
        output_configuration: {
          ...impactConfiguration.intervention_configuration.output_configuration,
          donate_button: false
        }
      }
    }} />;
    $[77] = donationPercentage;
    $[78] = impactConfiguration;
    $[79] = postTaxIncome;
    $[80] = t33;
  } else {
    t33 = $[80];
  }
  let t34;
  if ($[81] !== t29 || $[82] !== t32 || $[83] !== t33) {
    t34 = <div className={styles.wrapper}>{t29}{t32}{t33}</div>;
    $[81] = t29;
    $[82] = t32;
    $[83] = t33;
    $[84] = t34;
  } else {
    t34 = $[84];
  }
  return t34;
};
function _temp(total, value) {
  return total + value;
}
function _temp2(values) {
  return values.reduce(_temp, 0);
}
function _temp3(error) {
  console.error("Failed to calculate post-tax income", error);
}
function _temp4(value_0) {
  return typeof value_0 !== "undefined";
}